import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Chip,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  Switch,
  TablePagination,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import PlaceIcon from '@mui/icons-material/Place';
import { useNavigate } from 'react-router-dom';
import {
  useGetCustomersQuery,
  useGetCustomerQuery,
  useToggleCustomerStatusMutation,
} from '../../../store/api/salesApi';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';
import { useSelector, useDispatch } from 'react-redux';
import { setFilters } from '../../../store/slices/customersSlice';
import { CustomerFormContent } from './CustomerForm';

function CustomersList() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { search } = useSelector((state) => state.customers.filters);
  const [localSearch, setLocalSearch] = useState(search || '');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(setFilters({ search: localSearch }));
      setPage(0);
    }, 500);
    return () => clearTimeout(timer);
  }, [localSearch, dispatch]);

  const { data: company } = useGetCurrentCompanyQuery();
  const currency = company?.currency || 'USD';
  const formatAmount = (value) => {
    const amount = Number(value);
    if (Number.isNaN(amount)) return '–';
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency , minimumFractionDigits: (typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2), maximumFractionDigits: (typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2)}).format(amount);
    } catch {
      return `${currency} ${amount.toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}`;
    }
  };

  const { data, isLoading, error } = useGetCustomersQuery({
    page: page + 1,
    limit: rowsPerPage,
    search,
  });
  const [toggleStatus] = useToggleCustomerStatusMutation();
  const { data: editingCustomer, isLoading: loadingCustomer } = useGetCustomerQuery(editingId, {
    skip: !editingId,
  });

  const handleToggleStatus = (id) => {
    toggleStatus(id);
  };

  const customers = data?.data || (Array.isArray(data) ? data : (data?.customers || []));
  const totalCount = data?.total || customers.length;

  const handleOpenAdd = () => {
    setEditingId(null);
    setDialogOpen(true);
  };

  const handleOpenEdit = (id) => {
    setEditingId(id);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingId(null);
  };

  const handleFormSuccess = () => {
    handleCloseDialog();
  };

  if (isLoading) return <Typography>Loading...</Typography>;
  if (error) return <Typography color="error">Error loading customers</Typography>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
          Customers
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenAdd}
          sx={{ borderRadius: 2 }}
        >
          Add Customer
        </Button>
      </Box>

      <Box sx={{ mb: 3 }}>
        <TextField
          label="Search customers"
          variant="outlined"
          fullWidth
          value={localSearch}
          onChange={(e) => setLocalSearch(e.target.value)}
          placeholder="Search by name, email or code..."
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ maxWidth: 400 }}
        />
      </Box>

      <Card>
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Code</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Phone</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Address</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Location</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{`Credit Limit (${currency})`}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{`Opening Balance (${currency})`}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{`Current Balance (${currency})`}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {customers?.length > 0 ? (
                  customers.map((customer) => (
                    <TableRow key={customer.id} hover>
                      <TableCell sx={{ fontWeight: 500 }}>{customer.name}</TableCell>
                      <TableCell>{customer.code || '-'}</TableCell>
                      <TableCell>{customer.email || '-'}</TableCell>
                      <TableCell>{customer.phone || '-'}</TableCell>
                      <TableCell sx={{ maxWidth: 180 }}>{customer.address || '-'}</TableCell>
                      <TableCell>
                        {customer.locationUrl ? (
                          <IconButton
                            size="small"
                            color="primary"
                            component="a"
                            href={customer.locationUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="View location"
                          >
                            <PlaceIcon fontSize="small" />
                          </IconButton>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            –
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{formatAmount(customer.creditLimit ?? 0)}</TableCell>
                      <TableCell>{formatAmount(customer.openingBalance ?? 0)}</TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 500,
                          color: Number(customer.creditBalance) > 0 ? 'warning.main' : 'text.secondary',
                        }}
                      >
                        {formatAmount(customer.creditBalance ?? 0)}
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={!!customer.isActive}
                          onChange={() => handleToggleStatus(customer.id)}
                          color="primary"
                          size="small"
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: '#4ECDC4',
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: '#4ECDC4',
                            },
                          }}
                        />
                        <Typography component="span" variant="caption" sx={{ ml: 1 }}>
                          {customer.isActive ? 'Active' : 'Inactive'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => navigate(`/customers/${customer.id}/details`)}
                          sx={{ mr: 1 }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleOpenEdit(customer.id)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No customers found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </CardContent>
      </Card>

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 1.5,
            boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          {editingId ? 'Edit Customer' : 'Add Customer'}
        </DialogTitle>
        <DialogContent
          sx={{
            pt: 2,
            overflow: 'visible',
            '& .MuiInputLabel-root': {
              overflow: 'visible',
            },
          }}
        >
          <CustomerFormContent
            customer={editingId ? editingCustomer : null}
            isEdit={!!editingId}
            loading={!!editingId && loadingCustomer}
            onSuccess={handleFormSuccess}
            onCancel={handleCloseDialog}
            compact
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default CustomersList;
